import {useCallback, useEffect, useState} from 'react';
// Redux
import {useSelector, useDispatch} from 'react-redux';

import MealPlanAndRefundInfo from '../Shared/MealPlanAndRefundInfo/MealPlanAndRefundInfo';
import PaxInfo from '../Shared/PaxInfo/PaxInfo';
// Configs
import {appConfig, gaConfig} from '@stencil/hotels-config';

//styles
import * as S from './CartFlow.styles';

//thunks
import {fetchCartUpdatedPriceDataThunk} from '@stencil/hotels-cg-reducer/src/details/thunks';

// Components
import OfferLoader from '../../../../../../../stories/components/hotels/Loadingpage/OfferLoader/OfferLoader';
import AddUpgradeRoomFlow from '../Shared/AddUpgradeRoomFlow/AddUpgradeRoomFlow';
import BrandModalHeader from '../../../../../../components/BrandModalHeader';

import {multiRoomTextCreator, RoomCase} from '@stencil/hotels-utils/src/details-utils';
// Selectors
import {
  checkinSelectorFn,
  checkoutSelectorFn,
  cityIdSelectorFn,
  goingForwardPropertyIdSelector,
  roomStringSelectorFn,
} from '@stencil/hotels-common-reducer/src/selectors';
import {
  detailsUnifSelector as detailSelectorFn,
  isCartUpdatingStatusSelector,
  multiRoomCartDataSelector,
  cartResponseSelector,
  multiRoomArcInfo,
  occupancyRoomsSelector as multiRoomMPRLSelector,
  hotelNameSelector,
  isEntirePropertySelector,
  sellableTypeSelector,
  hotelLocationSelector,
  searchHotelDetailsSelector,
  luckyDataSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';
// Utils
import {
  getAdultandChildTxt,
  getRoomsandPaxCountInCartCg,
  getRoomsArrayForCartRooms,
  setARCObjCg,
} from '@stencil/hotels-utils/src/common-utils';
import dateformat from 'dateformat';
import splitRoomString from '@stencil/hotels-utils/src/split-room-string';
import {getSlashAndShowPriceCg} from '@stencil/hotels-utils/src/get-details-slash-and-show-price';
import Modal, {useModal} from '@stencil/hotels-modal-component';
import useNavigateToReviewPage from '@stencil/navigate-to-review-page-dweb';

// Ommniture
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {rupeeString} from '@stencil/hotels-config/src/config/app-config';

const CartFlow = ({noInclusionsAndSplInstructionsForAnyRoom = false, filterHeight}) => {
  const {
    isModalOpen: isLessOccupancyModalOpen,
    openModal: openLessOccupancyModal,
    closeModal: closeLessOccupancyModal,
  } = useModal();
  const {DetailsGAClickEventActions} = gaConfig;
  const {multiRoomEventActions} = DetailsGAClickEventActions;
  const [navigatorNew] = useNavigateToReviewPage();

  const isCartUpdating = useSelector(isCartUpdatingStatusSelector);
  const cartRoomData = useSelector(multiRoomCartDataSelector);
  const cartResponseData = useSelector(cartResponseSelector);
  const detailStoreData = useSelector(detailSelectorFn);
  const hotelName = useSelector(hotelNameSelector);
  const hotelCode = useSelector(goingForwardPropertyIdSelector);
  const checkinJSDate = useSelector(checkinSelectorFn).date;
  const checkoutJSDate = useSelector(checkoutSelectorFn).date;
  const hotelLocation = useSelector(hotelLocationSelector);
  const hotelArea = hotelLocation?.name;

  const searchRoomHotelDetails = useSelector(searchHotelDetailsSelector);

  const isUpSellFlow = true;

  let roomString = useSelector(roomStringSelectorFn);
  const arcInfoObj = useSelector(multiRoomArcInfo);
  const mprlData = useSelector(multiRoomMPRLSelector);
  const isEntireProperty = useSelector(isEntirePropertySelector);
  const sellableType = useSelector(sellableTypeSelector);
  const luckyData = useSelector(luckyDataSelector);

  const [newArcInfoObj, setNewArcInfoObj] = useState(arcInfoObj);
  const checkinYYYYMMDD = dateformat(checkinJSDate, 'yyyymmdd');
  const checkoutYYYYMMDD = dateformat(checkoutJSDate, 'yyyymmdd');
  const {paxCount, childCount} = splitRoomString(roomString);
  const {
    cartRequestPayload: requestData = {},
    cartRoomUIData: cartUIData = {},
    cartPayMode,
  } = detailStoreData;
  const dispatch = useDispatch();
  const [newRequestObj, setNewRequestObj] = useState(requestData);
  const [newCartUItObj, setNewCartUItObj] = useState(cartUIData);
  const [isCountClicked, setIsCountClicked] = useState(false);

  const checkTotalGuestReached = roomUIObject => {
    const {totalGuestCount} = getRoomsandPaxCountInCartCg(roomUIObject);
    const isTotalGuestCountExceeded = totalGuestCount > paxCount + childCount;
    return isTotalGuestCountExceeded;
  };

  const checkARCReached = (reqObject, roomCode) => {
    let obj = setARCObjCg(newArcInfoObj, roomCode, mprlData, reqObject);
    setNewArcInfoObj(obj);
  };

  const updateRoomPaxObj = (key, data, index) => {
    let roomData = cartRoomData[key]?.roomData;
    let roomCode = roomData?.roomTypeCode;
    let requestObj = {},
      cartUIObj = {},
      isTotalGuestCountExceeded = false;
    requestObj = {...requestData};
    cartUIObj = {...cartUIData};

    if (data) {
      let pwiData = cartRoomData[key]?.multiRoomPwi;
      requestObj[key] = data;
      cartUIObj[key] = {
        count: data?.count,
        roomData,
        multiRoomPwi: pwiData,
      };
    } else {
      delete requestObj[key];
      delete cartUIObj[key];
    }
    isTotalGuestCountExceeded = checkTotalGuestReached(cartUIObj);
    if (!isTotalGuestCountExceeded) {
      checkARCReached(requestObj, roomCode);
      setNewRequestObj(requestObj);
      setNewCartUItObj(cartUIObj);
      setIsCountClicked(true);
    }
    return isTotalGuestCountExceeded;
  };

  const updateCart = () => {
    dispatch(
      fetchCartUpdatedPriceDataThunk({
        requestData: newRequestObj,
        cartUIData: newCartUItObj,
        arcLimitData: newArcInfoObj,
      }),
    );
  };

  const goToReviewPage = (skipCount = false) => {
    const {totalGuestCount, totalAdultCount, totalChildCount} = getRoomOccupanyCount();
    const {showPrice} = getSlashAndShowPriceCg(cartResponseData);
    /**
     * Show Modal When Skip Count is true and either of the following conditions satisfy:
     * 1. Total Guest Searched is Less than Total Guest Count of Rooms in Cart.
     * 2. Total Guest Count Equals to Total Guest Count of Rooms in Cart
     *    but Pax Combination is different in cart and what is searched.
     */
    if (
      !skipCount &&
      (totalGuestCount < paxCount + childCount ||
        (totalGuestCount == paxCount + childCount &&
          totalAdultCount !== paxCount &&
          totalChildCount !== childCount))
    ) {
      openLessOccupancyModal();
    } else {
      let selectedRoomInfo = getRoomsArrayForCartRooms({cartUIData});
      navigatorNew({
        selectedRoomInfo: selectedRoomInfo,
        rootLevelInfo: {
          searchType: 'O',
        },
      });
      dispatch(omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.selectRoom));
    }
  };

  const getRoomOccupanyCount = () => {
    let totalAdultCount = 0,
      totalChildCount = 0,
      totalRoomCount = 0,
      propertyTypeTag = '',
      sellableType = '',
      isBed = false,
      isRoom = false;

    Object.keys(cartRoomData).map((item, index) => {
      let pwiData = cartRoomData[item]?.multiRoomPwi;
      let roomCount = cartRoomData[item]?.count;
      if (cartRoomData[item]?.roomData?.sellableType?.includes('bed')) {
        isBed = true;
      } else if (cartRoomData[item]?.roomData?.sellableType?.includes('room')) {
        isRoom = true;
      }
      propertyTypeTag = cartRoomData[item]?.roomData?.propertyTypeTag;
      sellableType = cartRoomData[item]?.roomData?.sellableType;
      totalRoomCount += roomCount;

      totalAdultCount += pwiData?.occupancydetails?.numberOfAdults * roomCount;
      totalChildCount += pwiData?.occupancydetails?.numberOfChildren * roomCount;
    });

    return {
      totalAdultCount,
      totalChildCount,
      totalRoomCount,
      totalGuestCount: totalAdultCount + totalChildCount,
      isBed,
      isRoom,
      propertyTypeTag,
      sellableType,
    };
  };

  const getModalContentTpl = () => {
    const {totalGuestCount, totalAdultCount, totalChildCount} = getRoomOccupanyCount();
    let tpl = null;
    if (totalGuestCount < paxCount + childCount) {
      tpl = (
        <>
          <S.LessOccupnacyModalInfoText>
            You had searched for {paxCount + childCount} Guests. Your have only selected
          </S.LessOccupnacyModalInfoText>
          <S.LessOccupnacyModalInfoText>
            {isEntireProperty ? 'Property' : getRoomOccupanyCount()?.isBed ? 'Beds' : 'Rooms'} to
            fit <S.AlertSpan>{getRoomOccupanyCount()?.totalGuestCount} Guests </S.AlertSpan>
          </S.LessOccupnacyModalInfoText>
        </>
      );
    } else if (
      totalGuestCount == paxCount + childCount &&
      totalAdultCount !== paxCount &&
      totalChildCount !== childCount
    ) {
      tpl = (
        <>
          <S.LessOccupnacyModalInfoText>
            You had searched for {getAdultandChildTxt(paxCount, childCount)}. But you have selected
          </S.LessOccupnacyModalInfoText>
          <S.LessOccupnacyModalInfoText>
            <S.AlertSpan>{getAdultandChildTxt(totalAdultCount, totalChildCount)} </S.AlertSpan>
          </S.LessOccupnacyModalInfoText>
        </>
      );
    }
    return tpl;
  };

  const getFinalCartPriceInfo = useCallback(() => {
    const {slashedPrice, showPrice, tnc, priceDisplayMsg} =
      getSlashAndShowPriceCg(cartResponseData);
    return {
      slashedPrice,
      showPrice,
      tnc,
      priceDisplayMsg,
    };
  }, [cartResponseData]);

  const multiRoomPaxText = multiRoomTextCreator({
    type: RoomCase.CartFlow,
    sellableType,
    isEntireProperty,
    totalRoomCount: getRoomOccupanyCount()?.totalRoomCount,
    propertyTypeTag: getRoomOccupanyCount()?.propertyTypeTag,
    isBed: getRoomOccupanyCount()?.isBed,
    isRoom: getRoomOccupanyCount()?.isRoom,
  });

  useEffect(() => {
    /**This is when Pax Variant is not shown in Modal
     * Instead displayed directly in Guests & Rooms Column,
     * then directly invoke the cart-discount API
     * **/
    if (isCountClicked) {
      updateCart();
      setIsCountClicked(false);
    }
  }, [newRequestObj, newCartUItObj]);

  return (
    <>
      {!isCartUpdating ? (
        // cartResponseData?.success &&
        cartRoomData && Object.keys(cartRoomData).length > 0 ? (
          <S.CartStickyHeaderDiv
            isGuestFilled={getRoomOccupanyCount()?.totalGuestCount >= paxCount + childCount}
            filterHeight={filterHeight}
            isUpSellFlow={isUpSellFlow}
          >
            <p>
              {' '}
              <S.FitRoomText>{multiRoomPaxText}</S.FitRoomText>
            </p>
            <p>
              Fits {getRoomOccupanyCount()?.totalGuestCount} Guests out of {paxCount + childCount}{' '}
              guests
            </p>
          </S.CartStickyHeaderDiv>
        ) : null
      ) : (
        <S.StickyHeaderLoaderWrapper filterHeight={filterHeight} isUpSellFlow={isUpSellFlow}>
          <S.StickyHeaderLoader />
        </S.StickyHeaderLoaderWrapper>
      )}
      <S.CartContainerWrapper isNoInclusion={noInclusionsAndSplInstructionsForAnyRoom}>
        <S.CartInnerWrapperDiv isFilterExists={filterHeight ? true : false}>
          {!isCartUpdating ? (
            // cartResponseData?.success &&
            cartRoomData && Object.keys(cartRoomData).length > 0 ? (
              <>
                <S.CartMainContainerWrapper>
                  {Object.keys(cartRoomData).map((item, index) => {
                    let roomData = cartRoomData[item]?.roomData;
                    let pwiData = cartRoomData[item]?.multiRoomPwi;
                    const {tnc, showPrice} = getSlashAndShowPriceCg(pwiData);
                    // let roomPriceData = pwiData?.pd || {};

                    return (
                      <S.CartRoomDetailsBlockDiv>
                        <S.RoomDetailsDiv>
                          <S.PropertyTypePara>{roomData?.roomTypeName}</S.PropertyTypePara>
                          <S.PaxDetailsDiv>
                            <S.PaxDetailsInnerDiv>
                              <S.FlexWrap>
                                <PaxInfo
                                  adultCount={pwiData?.occupancydetails?.numberOfAdults}
                                  childCount={pwiData?.occupancydetails?.numberOfChildren}
                                />
                              </S.FlexWrap>
                              <MealPlanAndRefundInfo
                                mealPlanData={roomData?.mt}
                                refundPlanData={roomData?.ct}
                              />
                            </S.PaxDetailsInnerDiv>
                          </S.PaxDetailsDiv>
                          <S.TaxAndInclusionPara>
                            <span>
                              <strong>
                                {' '}
                                {rupeeString} {showPrice}{' '}
                              </strong>
                              per night
                            </span>
                            {!!tnc && (
                              <S.FlexWrap>
                                + {rupeeString} {tnc} taxes &amp; fees{' '}
                              </S.FlexWrap>
                            )}
                          </S.TaxAndInclusionPara>
                          <div
                            style={{
                              width: '55%',
                            }}
                          >
                            <AddUpgradeRoomFlow
                              index={index}
                              rpc={roomData?.roomPriceCode}
                              rtc={roomData?.roomTypeCode}
                              pk={pwiData?.pk}
                              pwiDataItem={pwiData}
                              // pax={roomPriceData?.pax}
                              onRoomCountUpdate={updateRoomPaxObj}
                            />
                          </div>
                        </S.RoomDetailsDiv>
                      </S.CartRoomDetailsBlockDiv>
                    );
                  })}

                  <S.BookRoomWrapperDiv>
                    {getFinalCartPriceInfo()?.slashedPrice &&
                    getFinalCartPriceInfo()?.showPrice &&
                    getFinalCartPriceInfo()?.slashedPrice !== getFinalCartPriceInfo()?.showPrice ? (
                      <S.StrickedPricePara>
                        {' '}
                        {rupeeString}
                        {getFinalCartPriceInfo()?.slashedPrice}
                      </S.StrickedPricePara>
                    ) : null}

                    <S.OriginalPricePara>
                      {' '}
                      {rupeeString} {getFinalCartPriceInfo()?.showPrice}
                    </S.OriginalPricePara>
                    {!!getFinalCartPriceInfo()?.tnc && (
                      <S.TaxInclusionPara>
                        + {rupeeString} {getFinalCartPriceInfo()?.tnc} taxes &amp; fees{' '}
                      </S.TaxInclusionPara>
                    )}

                    <S.RoomsAvailablePara>
                      {' '}
                      {getFinalCartPriceInfo()?.priceDisplayMsg}
                    </S.RoomsAvailablePara>
                    <S.ComboBtn onClick={() => goToReviewPage()}>
                      <span>BOOK {multiRoomPaxText}</span>
                      <S.TotalPaxCountBtnSpan>
                        for{' '}
                        {getAdultandChildTxt(
                          getRoomOccupanyCount()?.totalAdultCount,
                          getRoomOccupanyCount()?.totalChildCount,
                        )}
                      </S.TotalPaxCountBtnSpan>
                    </S.ComboBtn>
                  </S.BookRoomWrapperDiv>
                </S.CartMainContainerWrapper>
              </>
            ) : null
          ) : (
            <div style={{marginTop: '140px'}}>
              {' '}
              <OfferLoader noBackGround={true} />
            </div>
          )}

          {!isCartUpdating &&
          (!cartRoomData || (cartRoomData && Object.keys(cartRoomData).length == 0)) ? (
            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                padding: '10px',
                position: 'sticky',
                top: '80rem',
              }}
            >
              <p
                style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  marginBottom: '20px',
                  textAlign: 'center',
                }}
              >
                No {isEntireProperty ? 'Property' : getRoomOccupanyCount()?.isBed ? 'Bed' : 'Room'}{' '}
                Selected
              </p>
            </div>
          ) : null}
        </S.CartInnerWrapperDiv>
        {/* Less Occupancy Count MODAL */}
        <Modal
          isOpen={isLessOccupancyModalOpen}
          closeModal={closeLessOccupancyModal}
          modalStyles={{width: '600px'}}
        >
          <BrandModalHeader style={{padding: '2rem'}}>Review Your Selection</BrandModalHeader>
          <S.LessOccupnacyModalContent>
            {getModalContentTpl()}
            <S.RoomSelectedListWrapper>
              {Object.keys(cartRoomData).map((item, index) => {
                let roomData = cartRoomData[item]?.roomData;
                let pwiData = cartRoomData[item]?.multiRoomPwi;
                let roomCount = cartRoomData[item]?.count;
                let roomPriceData = pwiData?.pd || {};

                return (
                  <S.RoomSelectedItem>
                    <S.BulletPointOuter>
                      <S.BulletPoint />
                    </S.BulletPointOuter>
                    <S.BulletTextOuter>
                      <S.BulletTextSpan>
                        {roomCount} {' x '} {roomData?.roomTypeName} {' - '}{' '}
                        {
                          <div
                            style={{
                              display: 'inline-block',
                            }}
                          >
                            <PaxInfo
                              adultCount={roomCount * pwiData?.occupancydetails?.numberOfAdults}
                              childCount={roomCount * pwiData?.occupancydetails?.numberOfChildren}
                              hideIcon={true}
                              marginLeft={'0px'}
                            />
                          </div>
                        }
                      </S.BulletTextSpan>
                    </S.BulletTextOuter>
                  </S.RoomSelectedItem>
                );
              })}
            </S.RoomSelectedListWrapper>
            <S.ModalActionContainer>
              <S.EditSelectionButton onClick={closeLessOccupancyModal}>
                EDIT SELECTION
              </S.EditSelectionButton>

              <S.ContinueAwayButton onClick={() => goToReviewPage(true)}>
                CONTINUE ANYWAY
                <S.ButtonGuestCount>
                  {getRoomOccupanyCount()?.totalGuestCount + ' Guests'}
                </S.ButtonGuestCount>
              </S.ContinueAwayButton>
            </S.ModalActionContainer>
          </S.LessOccupnacyModalContent>
        </Modal>
      </S.CartContainerWrapper>
    </>
  );
};

export default CartFlow;
